import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { lazy, Suspense } from "react";

// Eager load critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LoadingSpinner from "./components/LoadingSpinner";

// Lazy load non-critical pages
const StoriesIndex = lazy(() => import("./pages/StoriesIndex"));
const StoryDetail = lazy(() => import("./pages/StoryDetail"));
const Newsletter = lazy(() => import("./pages/Newsletter"));

const Kontakt = lazy(() => import("./pages/Kontakt"));
const Mediadaten = lazy(() => import("./pages/Mediadaten"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Impressum = lazy(() => import("./pages/Impressum"));
const AGB = lazy(() => import("./pages/AGB"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminArticlesNew = lazy(() => import("./pages/AdminArticlesNew"));
const AdminBannersNew = lazy(() => import("./pages/AdminBannersNew"));
const AdminNewsletterNew = lazy(() => import("./pages/AdminNewsletterNew"));
const AdminMedia = lazy(() => import("./pages/AdminMedia"));
const AdminMediaLogs = lazy(() => import("./pages/AdminMediaLogs"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminNewsletterAnalytics = lazy(() => import("./pages/AdminNewsletterAnalytics"));
const AdminSecurity = lazy(() => import("./pages/AdminSecurity"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminPrintToWeb = lazy(() => import("./pages/AdminPrintToWeb"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const UnsubscribeVerify = lazy(() => import("./pages/UnsubscribeVerify"));
const BNI = lazy(() => import("./pages/BNI"));
const ProNordic = lazy(() => import("./pages/ProNordic"));
const NordicAcademy = lazy(() => import("./pages/NordicAcademy"));
const Ostern = lazy(() => import("./pages/Ostern"));
const Langlaufcenter = lazy(() => import("./pages/Langlaufcenter"));
const Friends = lazy(() => import("./pages/Friends"));
const Loipen = lazy(() => import("./pages/Loipen"));
const TheCard = lazy(() => import("./pages/TheCard"));
const Probeausgabe = lazy(() => import("./pages/Probeausgabe"));
const SpotMagazin = lazy(() => import("./pages/SpotMagazin"));
const Leserangebot = lazy(() => import("./pages/Leserangebot"));
const Magazine = lazy(() => import("./pages/Magazine"));
const Shop = lazy(() => import("./pages/Shop"));
const BlogPreview = lazy(() => import("./pages/BlogPreview"));
const EmailPreview = lazy(() => import("./components/EmailPreview"));
const OrderConfirmationPreview = lazy(() => import("./components/OrderConfirmationPreview"));
const Umfrage = lazy(() => import("./pages/Umfrage"));
const SurveyResults = lazy(() => import("./pages/SurveyResults"));
const AdminSurvey = lazy(() => import("./pages/AdminSurvey"));
const FreelancerSubmit = lazy(() => import("./pages/FreelancerSubmit"));
const AdminCommerce = lazy(() => import("./pages/AdminCommerce"));
const ShopTestLayout = lazy(() => import("./pages/shop-test/ShopTestLayout"));
const ShopTestIndex = lazy(() => import("./pages/shop-test/ShopTestIndex"));
const ShopTestProduct = lazy(() => import("./pages/shop-test/ShopTestProduct"));
const ShopTestCategory = lazy(() => import("./pages/shop-test/ShopTestCategory"));
const ShopTestCart = lazy(() => import("./pages/shop-test/ShopTestCart"));
const ShopTestCheckout = lazy(() => import("./pages/shop-test/ShopTestCheckout"));
const ShopTestOrderSuccess = lazy(() => import("./pages/shop-test/ShopTestOrderSuccess"));
const ShopTestOrderFailed = lazy(() => import("./pages/shop-test/ShopTestOrderFailed"));


import { useAnalytics } from "./hooks/useAnalytics";
import CookieBanner from "./components/CookieBanner";

const queryClient = new QueryClient();

const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AnalyticsTracker />
            <CookieBanner />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/stories" element={<StoriesIndex />} />
                <Route path="/stories/:slug" element={<StoryDetail />} />
                <Route path="/newsletter" element={<Newsletter />} />
                
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/mediadaten" element={<Mediadaten />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/articles" element={<AdminArticlesNew />} />
                <Route path="/admin/newsletter" element={<AdminNewsletterNew />} />
                <Route path="/admin/newsletter-analytics" element={<AdminNewsletterAnalytics />} />
                <Route path="/admin/banners" element={<AdminBannersNew />} />
                <Route path="/admin/media" element={<AdminMedia />} />
                <Route path="/admin/media-logs" element={<AdminMediaLogs />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/security" element={<AdminSecurity />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/print-to-web" element={<AdminPrintToWeb />} />
                <Route path="/admin/commerce" element={<AdminCommerce />} />
                {/* Interner Test-Shop – nicht verlinkt, nicht indexiert */}
                <Route path="/shop-test" element={<ShopTestLayout />}>
                  <Route index element={<ShopTestIndex />} />
                  <Route path="kategorie/:slug" element={<ShopTestCategory />} />
                  <Route path="produkt/:slug" element={<ShopTestProduct />} />
                  <Route path="warenkorb" element={<ShopTestCart />} />
                  <Route path="checkout" element={<ShopTestCheckout />} />
                  <Route path="bestellung/:id/erfolgreich" element={<ShopTestOrderSuccess />} />
                  <Route path="bestellung/:id/fehlgeschlagen" element={<ShopTestOrderFailed />} />
                </Route>
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/unsubscribe/verify" element={<UnsubscribeVerify />} />
                <Route path="/loipen" element={<Loipen />} />
                <Route path="/thecard" element={<TheCard />} />
                <Route path="/spotmagazin" element={<SpotMagazin />} />
                <Route path="/langlaufcenter" element={<Langlaufcenter />} />
                <Route path="/friends" element={<Friends />} />
                <Route path="/freelancer-artikel-einreichen" element={<FreelancerSubmit />} />

                <Route path="/bni" element={<BNI />} />
                <Route path="/pronordic" element={<ProNordic />} />
                <Route path="/nordic" element={<NordicAcademy />} />
                <Route path="/ostern" element={<Ostern />} />
                <Route path="/probeausgabe" element={<Probeausgabe />} />
                <Route path="/leserangebot" element={<Leserangebot />} />
                <Route path="/magazin" element={<Magazine />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/preview" element={<BlogPreview />} />
                <Route path="/blog/preview" element={<BlogPreview />} />
                <Route path="/preview-newsletter" element={<EmailPreview />} />
                <Route path="/preview-loipen" element={<OrderConfirmationPreview />} />
                <Route path="/umfrage" element={<Umfrage />} />
                <Route path="/umfrage-ergebnisse" element={<SurveyResults />} />
                <Route path="/admin/survey" element={<AdminSurvey />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;